import React, { ChangeEvent } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Box, useTheme } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { tokens } from "../../theme";

interface PhoneInputFieldProps {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium";
  label?: string | "";
  variant?: "filled" | "outlined" | "standard" | undefined;
  name: string;
  control: Control<any, any>;
  rules?: Object;
  isDisabled?: boolean;
  isEmpty?: boolean;
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  value,
  onChange,
  size,
  color,
  label,
  variant,
  name,
  control,
  rules,
  isDisabled,
  isEmpty,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isRequired = () => {
    if (rules && typeof rules === "object" && "required" in rules && label) {
      return true;
    }
    return false;
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return "";
    
    const cleaned = ("" + phone).replace(/\D/g, "");
    
    let countryCode = "";
    let mainNumber = cleaned;
    
    if (cleaned.length > 10) {
      countryCode = cleaned.substring(0, cleaned.length - 10);
      mainNumber = cleaned.slice(cleaned.length - 10);
    }
    
    const match = mainNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
    const formattedMainNumber = match ? `(${match[1]}) ${match[2]}-${match[3]}` : mainNumber;
    return countryCode ? `+${countryCode} ${formattedMainNumber}` : formattedMainNumber;
  }
  

  return (
    <Controller
      name={name}
      control={control}
      rules={rules || {}}
      render={({ field, fieldState }) => (
        <Box
          sx={{
            width: "100%",
            ...(theme.palette.mode === "dark" && {
              "& .flag-dropdown, & .flag-dropdown:hover, & .flag-dropdown.open": {
                backgroundColor: "transparent !important",
                border: "none !important",
                borderRight: "1px solid rgba(255,255,255,0.18) !important",
                borderRadius: "8px 0 0 8px !important",
              },
              "& .selected-flag, & .selected-flag:hover, & .selected-flag:focus, & .flag-dropdown.open .selected-flag":
                {
                  backgroundColor: "transparent !important",
                  borderRadius: "8px 0 0 8px !important",
                  padding: "0 !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
              "& .selected-flag .flag": {
                position: "static !important",
                margin: "0 !important",
              },
            }),
          }}
        >
          <PhoneInput
            country={"us"}
          //CS-1533 - Start
          onlyCountries={["us"]}
          disableDropdown={true}
          countryCodeEditable={false}
          value={isEmpty ? "" : value || field.value}
          onChange={(phone) => {
            const formattedPhone = formatPhoneNumber(phone);
            const event = {
              target: { value: formattedPhone } as HTMLInputElement,
            } as ChangeEvent<HTMLInputElement>;
            field.onChange(formattedPhone);
            onChange && onChange(event);
          }}          
          disabled={isDisabled}
          inputStyle={{
            width: "100%",
            height: "50px",
            borderRadius: "8px",
            borderWidth: "2px",
            borderColor: colors.primary[400],
            backgroundColor: "transparent",
            color: colors.grey[100],
          }}
          containerClass={
            theme.palette.mode === "dark"
              ? "phone-input-container-dark"
              : "phone-input-container-light"
          }
          buttonClass={
            theme.palette.mode === "dark"
              ? "phone-input-button-dark"
              : "phone-input-button-light"
          }
          dropdownClass={
            theme.palette.mode === "dark"
              ? "phone-input-dropdown-dark"
              : "phone-input-dropdown-light"
          }
          inputProps={{
            name,
            required: isRequired(),
            //CS-1533 - Added validation
            pattern: "^\\+?1?[^0-9]*([0-9][^0-9]*){10}$",
            title: "Enter a valid phone number with 10 digits",
          }}
          />
        </Box>
      )}
    />
  );
};

export default PhoneInputField;
